import React, { useState, useEffect } from 'react';
import TailwindModal from './TailwindModal';
import LoaderIcon from './LoaderIcon';
import UserAvatar from './UserAvatar';
import moment from "moment";
import { ClipboardList, Users, ShieldCheck } from "lucide-react";

const AcceptAllLeavesModal = ({ isOpen, onClose, onConfirm, leaves = [], loading }) => {
    const selectableLeaves = leaves.filter(r => !r.REQUEST_TYPE);

    // Default all selectable selected when modal opens
    const [selectedIds, setSelectedIds] = useState(new Set());

    useEffect(() => {
        if (isOpen) {
            setSelectedIds(new Set(selectableLeaves.map(r => r.REQUEST_ID)));
        }
    }, [isOpen, leaves]);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(new Set(selectableLeaves.map(r => r.REQUEST_ID)));
        } else {
            setSelectedIds(new Set());
        }
    };

    const handleSelectOne = (id) => {
        const newSelected = new Set(selectedIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedIds(newSelected);
    };

    const uniqueEmployeesCount = new Set(selectableLeaves.map(r => r.EMP_CODE)).size;
    const isAllSelected = selectableLeaves.length > 0 && selectedIds.size === selectableLeaves.length;
    const isSomeSelected = selectedIds.size > 0 && selectedIds.size < selectableLeaves.length;

    const handleConfirm = () => {
        const selectedLeaves = leaves.filter(r => selectedIds.has(r.REQUEST_ID));
        onConfirm(selectedLeaves);
    };

    return (
     <TailwindModal isOpen={isOpen} onClose={onClose} maxWidth="4xl">
      <div className="w-full overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 sm:pb-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-100">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <h2 className="text-base sm:text-lg font-semibold text-slate-900 mt-0.5 sm:mt-0">
              Accept All Pending Leave Requests
            </h2>
            <p className="text-[12px] leading-6 text-slate-500 hidden sm:block">
              You are about to accept pending leave requests. Please select the requests below and review before confirming.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="border-t border-slate-100">
          <div className="flex flex-col">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-100 bg-white gap-4 sm:gap-0">
              <h3 className="text-[15px] font-bold text-slate-800 m-0 hidden sm:block">Pending Leave Requests</h3>
              <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
                <div className="flex items-center gap-2 sm:gap-3 bg-slate-50 border border-slate-100 rounded-lg px-3 sm:px-4 py-2 flex-1 sm:flex-none justify-center sm:justify-start">
                  <div className="bg-blue-100 text-blue-600 rounded p-1.5 shadow-sm hidden sm:block"><ClipboardList size={16} /></div>
                  <div className="flex flex-col justify-center leading-tight">
                    <span className="text-[10px] sm:text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-center sm:text-left">Total Requests</span>
                    <span className="text-[14px] sm:text-[15px] font-bold text-blue-700 text-center sm:text-left">{selectableLeaves.length}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 bg-slate-50 border border-slate-100 rounded-lg px-3 sm:px-4 py-2 flex-1 sm:flex-none justify-center sm:justify-start">
                  <div className="bg-purple-100 text-purple-600 rounded p-1.5 shadow-sm hidden sm:block"><Users size={16} /></div>
                  <div className="flex flex-col justify-center leading-tight">
                    <span className="text-[10px] sm:text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-center sm:text-left">Employees</span>
                    <span className="text-[14px] sm:text-[15px] font-bold text-purple-700 text-center sm:text-left">{uniqueEmployeesCount}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="max-h-[350px] overflow-y-auto bg-white">
              <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50/80 sticky top-0 z-10 border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-3 sm:px-6 w-12 text-center">
                      <input 
                         type="checkbox" 
                         disabled={selectableLeaves.length === 0}
                         className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" 
                         checked={isAllSelected} 
                         ref={input => {
                            if (input) input.indeterminate = isSomeSelected;
                         }}
                         onChange={handleSelectAll} 
                      />
                    </th>
                    <th className="py-3 px-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Employee</th>
                    <th className="hidden sm:table-cell py-3 px-6 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Employee ID</th>
                    <th className="py-3 px-2 sm:px-6 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Leave Dates</th>
                    {/* <th className="py-3 px-6 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Leave Type</th> */}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leaves.map((row) => {
                    const isDeleteReq = !!row.REQUEST_TYPE;
                    return (
                    <tr key={row.REQUEST_ID} className="hover:bg-slate-50 transition-colors">
                      <td className="py-4 px-3 sm:px-6 text-center">
                        <input 
                           type="checkbox" 
                           disabled={isDeleteReq}
                           className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" 
                           checked={selectedIds.has(row.REQUEST_ID)}
                           onChange={() => handleSelectOne(row.REQUEST_ID)}
                        />
                      </td>
                      <td className="py-4 px-2">
                        <div className="flex items-center gap-3">
                          <div className="hidden sm:block"><UserAvatar name={row.EMP_NAME} size={38} /></div>
                          <div className="flex flex-col justify-center">
                            <span className="text-[13px] sm:text-sm font-bold text-slate-800 leading-tight">{row.EMP_NAME}</span>
                            <div className="flex items-center gap-2 mt-1">
                               <span className="text-[11px] sm:text-[12px] font-medium text-slate-500 hidden sm:block">{row.EMP_MAIL || row.EMP_WORK_EMAIL || ''}</span>
                               {isDeleteReq ? (
                                   <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 text-[9px] sm:text-[10px] font-bold w-max">
                                     <div className="w-1 h-1 rounded-full bg-orange-500"></div>
                                     Awaiting Delete
                                   </div>
                               ) : (
                                   <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 text-[9px] sm:text-[10px] font-bold w-max">
                                     <div className="w-1 h-1 rounded-full bg-blue-500"></div>
                                     Pending Approval
                                   </div>
                               )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="hidden sm:table-cell py-4 px-6 text-sm font-medium text-slate-700">{row.EMP_ID || 'N/A'}</td>
                      <td className="py-4 px-2 sm:px-6">
                        <div className="flex flex-col justify-center">
                          <span className="text-[11px] sm:text-[13px] font-semibold leading-tight text-slate-700">
                            {moment(row.START_DATE).format("DD MMM YY")} - {moment(row.END_DATE).format("DD MMM YY")}
                          </span>
                          <span className="text-[10px] sm:text-[12px] font-medium mt-1 text-slate-500">({row.NO_OF_DAYS} {row.NO_OF_DAYS > 1 ? 'days' : 'day'})</span>
                        </div>
                      </td>
                      {/* <td className="py-4 px-6 text-[13px] font-medium text-slate-600">
                          <div className="flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                              {row.LEAVE_TYPE}
                          </div>
                      </td> */}
                    </tr>
                    );
                  })}
                  {leaves.length === 0 && (
                      <tr>
                          <td colSpan="5" className="py-8 text-center text-sm text-slate-500">No pending leave requests found.</td>
                      </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-between border-t border-slate-200 bg-slate-50 px-4 sm:px-6 py-4 gap-4 sm:gap-0">
          <div className="flex-1 hidden sm:block">
            <div className="flex items-center gap-2.5 text-blue-600">
                <ShieldCheck size={20} className="shrink-0" />
                <span className="text-[13px] font-medium">Selected leave requests ({selectedIds.size}) will be accepted and balances updated.</span>
            </div>
          </div>
          
          <div className="flex justify-between sm:justify-end gap-3 shrink-0 sm:ml-4 w-full sm:w-auto">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 sm:flex-none rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 transition-all duration-200 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              onClick={handleConfirm}
              disabled={loading || selectedIds.size === 0}
              className="flex-1 sm:flex-none inline-flex min-w-[120px] items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-medium text-white transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <>
                  <LoaderIcon className="h-4 w-4" />
                  Processing...
                </>
              ) : (
                'Accept Selected'
              )}
            </button>
          </div>
        </div>
      </div>
    </TailwindModal>
    );
};

export default AcceptAllLeavesModal;
